/**
 * Cache for user role checks to prevent duplicate Supabase queries
 * Uses sessionStorage for persistence across page refreshes
 */

import type { SupabaseClient } from '@supabase/supabase-js'

interface RoleCacheEntry {
  roleId: number | null
  timestamp: number
  userId: string
}

class UserRoleCache {
  private readonly STORAGE_KEY = 'joulesadmin_role_cache'
  private readonly TTL = 300000 // Cache for 5 minutes (reasonable for a session)
  
  /**
   * Get cache from sessionStorage
   */
  private getStorageCache(): Map<string, RoleCacheEntry> {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return new Map(Object.entries(parsed))
      }
    } catch (error) {
      console.error('Error reading role cache from storage:', error)
    }
    return new Map()
  }
  
  /**
   * Save cache to sessionStorage
   */
  private saveStorageCache(cache: Map<string, RoleCacheEntry>): void {
    try {
      const obj = Object.fromEntries(cache)
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(obj))
    } catch (error) {
      console.error('Error saving role cache to storage:', error)
    }
  }
  
  /**
   * Get cached role ID for a user
   */
  getCachedRole(userId: string): number | null | undefined {
    const cache = this.getStorageCache()
    const entry = cache.get(userId)
    if (!entry) return undefined
    
    // Check if cache is still valid
    if (Date.now() - entry.timestamp > this.TTL) {
      cache.delete(userId)
      this.saveStorageCache(cache)
      return undefined
    }
    
    return entry.roleId
  }
  
  /**
   * Set cached role ID for a user
   */
  setCachedRole(userId: string, roleId: number | null): void {
    const cache = this.getStorageCache()
    cache.set(userId, {
      roleId,
      timestamp: Date.now(),
      userId
    })
    this.saveStorageCache(cache)
  }
  
  /**
   * Clear cache for a specific user
   */
  clearUser(userId: string): void {
    const cache = this.getStorageCache()
    cache.delete(userId)
    this.saveStorageCache(cache)
  }
  
  /**
   * Clear entire cache
   */
  clearAll(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing role cache:', error)
    }
  }
}

// Export singleton instance
export const userRoleCache = new UserRoleCache()

/**
 * Check if a user is a super admin with caching
 */
export async function checkSuperAdminRole(
  supabase: SupabaseClient,
  userId: string,
  forceRefresh = false
): Promise<boolean> {
  const superAdminRoleId = Number(import.meta.env.VITE_SUPER_ADMIN_ROLE_ID || 42)

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cachedRole = userRoleCache.getCachedRole(userId)
    if (cachedRole !== undefined) {
      console.log('[RoleCache] Using cached role for user:', userId)
      return cachedRole === superAdminRoleId
    }
  }

  // Query Supabase
  console.log('[RoleCache] Fetching role from Supabase for user:', userId)
  const { data, error } = await supabase
    .from('users')
    .select('role_id')
    .eq('id', userId)
    .single()

  if (error) {
    console.error('Error fetching user role:', error)
    return false
  }

  // Cache the result
  const roleId = data?.role_id || null
  userRoleCache.setCachedRole(userId, roleId)

  return roleId === superAdminRoleId
}

/**
 * Check if a user has access to the admin panel
 * Admin panel access is granted to:
 * - Super admins (role_id 42)
 * - Marketers (role_id 73)
 * - Admin users (role_id 7)
 * Or any user with blog-related permissions
 */
export async function checkAdminPanelAccess(
  supabase: SupabaseClient,
  userId: string
): Promise<boolean> {
  try {
    // First check if user has any role with blog permissions or is super admin
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        role_id,
        roles!inner (
          name,
          role_permissions!inner (
            permissions!inner (
              name
            )
          )
        )
      `)
      .eq('user_id', userId);

    if (error) {
      console.error('Error checking admin panel access:', error);
      return false;
    }

    if (!data || data.length === 0) {
      return false;
    }

    // Check if user has system.override (super admin) or any blog permission
    for (const userRole of data) {
      const roleData = userRole.roles as any;
      if (roleData && roleData.role_permissions) {
        for (const rp of roleData.role_permissions) {
          const permissionName = rp.permissions?.name;
          if (
            permissionName === 'system.override' ||
            permissionName?.includes('blog')
          ) {
            return true;
          }
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Failed to check admin panel access:', error);
    return false;
  }
}

/**
 * Get the default route for a user based on their permissions
 * Returns:
 * - '/blog/posts' for Marketers (users with blog permissions but not system.override)
 * - '/' for Super Admins and Admins (users with system.override)
 */
export async function getUserDefaultRoute(
  supabase: SupabaseClient,
  userId: string
): Promise<string> {
  try {
    // First check the user's primary role_id from users table
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role_id')
      .eq('id', userId)
      .single();

    console.log('[getUserDefaultRoute] User primary role:', { userData, userError });

    // If user has Superadmin role (42), send to dashboard
    if (userData?.role_id === 42) {
      console.log('[getUserDefaultRoute] User has primary Superadmin role (42), routing to /');
      return '/';
    }

    // Check user_roles table for additional permissions
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        role_id,
        roles!inner (
          name,
          role_permissions!inner (
            permissions!inner (
              name
            )
          )
        )
      `)
      .eq('user_id', userId);

    console.log('[getUserDefaultRoute] user_roles query result:', { data, error, userId });

    if (error || !data || data.length === 0) {
      console.log('[getUserDefaultRoute] No user_roles data, defaulting to /');
      // Default to dashboard if can't determine
      return '/';
    }

    let hasBlogPermissions = false;
    let hasSuperAdminPermission = false;

    // Check permissions and roles
    for (const userRole of data) {
      // Check if user has Superadmin role (role_id 42)
      if (userRole.role_id === 42) {
        console.log('[getUserDefaultRoute] User has Superadmin role (42) in user_roles');
        hasSuperAdminPermission = true;
      }

      const roleData = userRole.roles as any;
      if (roleData && roleData.role_permissions) {
        for (const rp of roleData.role_permissions) {
          const permissionName = rp.permissions?.name;
          console.log('[getUserDefaultRoute] Found permission:', permissionName);

          if (permissionName === 'system.override') {
            hasSuperAdminPermission = true;
          }

          if (permissionName?.includes('blog')) {
            hasBlogPermissions = true;
          }
        }
      }
    }

    console.log('[getUserDefaultRoute] Permission check results:', {
      hasSuperAdminPermission,
      hasBlogPermissions
    });

    // Super admins go to dashboard
    if (hasSuperAdminPermission) {
      console.log('[getUserDefaultRoute] User is super admin, routing to /');
      return '/';
    }

    // Marketers (blog permissions only) go to blog posts
    if (hasBlogPermissions) {
      console.log('[getUserDefaultRoute] User has blog permissions, routing to /blog/posts');
      return '/blog/posts';
    }

    // Default fallback
    console.log('[getUserDefaultRoute] No specific permissions found, defaulting to /');
    return '/';
  } catch (error) {
    console.error('Failed to get default route:', error);
    return '/';
  }
}